import React, { useState, useEffect, useContext, useCallback, memo } from "react";
import axios from 'axios';
import { AuthContext } from "../../contexts/AuthContext"; 
import DOMPurify from 'dompurify';
import { formatDistanceToNow } from 'date-fns'; 

const CommentForm = memo(({ handleCommentSubmit, replyToId, setReplyToId }) => {
    const [localNewComment, setLocalNewComment] = useState('');

    const handleLocalCommentChange = (event) => {
        setLocalNewComment(event.target.value);
    };

    const submitComment = (event) => {
        event.preventDefault();
        handleCommentSubmit(localNewComment);
        setLocalNewComment('');
    };

    return (
        <form onSubmit={submitComment} className="mt-4">

      {replyToId && <p className="mb-2 text-gray-800">Reply or <button onClick={() => setReplyToId(null)} className="text-blue-500 hover:underline">Cancel</button></p>}
      <textarea value={localNewComment} onChange={handleLocalCommentChange} placeholder="Add a comment..." className="w-full p-3 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
      <button type="submit" className="w-full md:w-80 mt-8 px-8 py-2 font-semibold rounded border border-gray-300 hover:bg-gray-600 hover:text-white transition-colors duration-200">{replyToId ? 'Post Reply' : 'Post Comment'}</button>
    </form>

    );
});

const CommentSection = ({ articleId, handleModal, onCommentCountUpdate }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [replyToId, setReplyToId] = useState(null);  
  const [showComments, setShowComments] = useState(false);  // New state to toggle visibility
  const { isAuthenticated } = useContext(AuthContext);
  const [message, setMessage] = useState('');    // Feedback message
  const [messageType, setMessageType] = useState('');

  const handleCommentChange = useCallback((event) => {
    setNewComment(event.target.value);
}, []);


  // Define fetchComments outside the useEffect
  const fetchComments = async () => {
    try {
      const response = await axios.get(`/api/comments/${articleId}`, {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });
      setComments(response.data);
    } catch (error) {
      //console.error(`Error fetching comments: ${error}`);
    }
  };

  useEffect(() => {

    if (!isAuthenticated) return;

    const timerId = setTimeout(fetchComments, 2000); // 2 seconds delay
    return () => clearTimeout(timerId); // Clear the timer when the component unmounts
    
  }, [articleId]);

  const MAX_COMMENT_LENGTH = 5000;

  const handleCommentSubmit = useCallback(async (commentText) => {

    if (!isAuthenticated) return;

    // Basic content validation
    if (!commentText.trim()) {
        alert('Comment content cannot be empty.');
        return;
    }

    if (commentText.length > MAX_COMMENT_LENGTH) {
        alert(`Comment content cannot exceed ${MAX_COMMENT_LENGTH} characters.`);
        return;
    }

    const sanitizedContent = DOMPurify.sanitize(commentText);

    // Optimistic update
    const optimisticComment = {
        id: Date.now(), // Temporary ID, assuming the backend assigns unique IDs
        comment_author: 'Current User', // This would ideally be the current user's username
        content: sanitizedContent,
        date_posted: new Date().toISOString(), // Current date/time
        // ... any other necessary fields with default values ...
    };

    setComments(prevComments => {
       return [...prevComments, optimisticComment];
    });

    try {
        const response = await axios.post('/api/comments', {
          postId: articleId,
          content: sanitizedContent,
          parentCommentId: replyToId  
        }, {
            headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });


        const persistedComment = response.data;

        setComments(prevComments => prevComments.map(comment => 
            comment.id === optimisticComment.id ? persistedComment : comment
        ));
        
        // Successful request: fetch fresh comments from server
        await fetchComments();  

        // Clearing the message after a successful request
        setMessage('Comment posted successfully!');
        setMessageType('success');

        setNewComment('');  
        setReplyToId(null);

    } catch (error) {
        // Remove optimistically added comment
        setComments(prevComments => prevComments.filter(comment => comment.id !== optimisticComment.id));

        //console.error(`Error posting comment: ${error}`);
        setMessage('Error posting your comment. Please try again later.');
        setMessageType('error');
    }
}, [replyToId, articleId]);

  const initiateReply = (commentId) => {
    setReplyToId(commentId);
  };

  const voteComment = async (commentId, voteType) => {
    const voteValue = voteType === 'upvote' ? 1 : -1;

    // Optimistic update considering switching and cancelling votes
    const updatedComments = comments.map(comment => {
      if (comment.id === commentId) {
        let adjustedVoteCount = comment.vote_count;
        
        if (comment.currentUserVote === voteValue) { 
          // Cancel the vote
          adjustedVoteCount -= voteValue;
          return { ...comment, currentUserVote: 0, vote_count: adjustedVoteCount };
        } else if (comment.currentUserVote === -voteValue) { 
          // Switch the vote
          adjustedVoteCount += 2 * voteValue;
          return { ...comment, currentUserVote: voteValue, vote_count: adjustedVoteCount };
        } else {
          // Normal vote
          adjustedVoteCount += voteValue;
          return { ...comment, currentUserVote: voteValue, vote_count: adjustedVoteCount };
        }
      }
      return comment;
    });
    setComments(updatedComments);

    try {
      await axios.post(`/api/comments/${commentId}/vote`, {
        vote: voteValue
      }, {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      await fetchComments(); // Refresh comments to get exact updated vote counts from backend
    } catch (error) {
      //console.error(`Error ${voteType}ing comment: ${error}`);
      // Revert the optimistic update if error occurs
      setComments(comments);
      alert('There was an issue processing your vote. Please try again.');
    }
  };


  const ArrowContainer = ({ children }) => (
    <div className="flex flex-col items-center justify-center">
      {children}
    </div>
  );

  const UpArrow = ({ comment }) => (
    <ArrowContainer>
      <svg
        width="17"
        height="17"
        viewBox="0 0 24 24"
        fill={comment.currentUserVote === 1 ? "green" : "grey"}
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M12 6L4 14H20L12 6Z"></path>
      </svg>
      <span className="mt-1">{comment.vote_count}</span>
    </ArrowContainer>
  );

  const DownArrow = ({ comment }) => (
    <ArrowContainer>
      <svg
        width="17"
        height="17"
        viewBox="0 0 24 24"
        fill={comment.currentUserVote === -1 ? "red" : "grey"}
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M12 18L20 10L4 10L12 18Z"></path>
      </svg>
    </ArrowContainer>
  );

  useEffect(() => {
    // This useEffect will trigger whenever 'comments' changes.
    onCommentCountUpdate(comments.length);
  }, [comments, onCommentCountUpdate]);

  const Comment = ({ comment, isAuthenticated, initiateReply, voteComment }) => {

    const distance = formatDistanceToNow(new Date(comment.date_posted));
              const cleanedDistance = distance.replace('about ', '');
    return (
      <div className="comment p-3 rounded-md mb-4 shadow-sm">
        <div className="flex items-start">
          {isAuthenticated && (
            <div className="mr-3 flex flex-col items-center">
              <button 
                onClick={() => voteComment(comment.id, 'upvote')} 
                className={`mb-1 ${comment.currentUserVote === 1 ? 'text-green-700' : 'text-gray-400'} hover:text-green-600`}
              >
                <UpArrow comment={comment} />
              </button>
              <button 
                onClick={() => voteComment(comment.id, 'downvote')} 
                className={`${comment.currentUserVote === -1 ? 'text-red-700' : 'text-gray-400'} hover:text-red-600`}
              >
                <DownArrow comment={comment} />
              </button>
            </div>
          )}
          
          <div className="ml-4">
            <p className="dark:text-white">
              <strong className="dark:text-white">{comment.comment_author}</strong>

              <small className="text-gray-500 ml-4">
                  {cleanedDistance} ago
              </small>

            </p>

            <p className="mt-2 dark:text-white">{comment.content}</p>

            {isAuthenticated && (
              <button 
                  onClick={() => initiateReply(comment.id)}
                  className="text-sm text-gray-500 hover:underline"
              >
                  reply
              </button>
          )}
          </div>
        </div>
        
        {comment.replies && comment.replies.map(reply => (
          <div key={reply.id} className="comment reply mt-3 ml-6 border-l border-gray-200 pl-4">
            <Comment comment={reply} isAuthenticated={isAuthenticated} initiateReply={initiateReply} voteComment={voteComment} />
          </div>
        ))}

        {isAuthenticated && replyToId === comment.id && (
          <CommentForm 
            handleCommentSubmit={handleCommentSubmit} 
            newComment={newComment} 
            handleCommentChange={handleCommentChange} 
            replyToId={replyToId} 
            setReplyToId={setReplyToId} 
          />
        )}
      </div>
    );
  }

  return (
    <div className="comment-section p-4 rounded-md">
      {/* Feedback message display */}
      {message && (
        <div className={`alert ${messageType === 'success' ? 'bg-green-200 text-green-700' : 'bg-red-200 text-red-700'} p-3 rounded-md mb-4`}>
          {message}
        </div>
      )}

      {/* Button to toggle comment visibility */}
      <button onClick={() => setShowComments(!showComments)} className="w-full mt-1 py-2 font-semibold rounded border border-gray-300 hover:bg-gray-600 hover:text-white transition-colors duration-200">
        {comments.length === 0 ? "Comment" : comments.length === 1 ? "1 Comment" : `${comments.length} Comments`}
      </button>

      {showComments && !isAuthenticated && (
        <div className="mt-4">
          <p>Want to join the discussion? 
          <button className="text-blue-500 ml-2 mr-2 hover:underline" onClick={() => handleModal('login')}>Login</button>
            to comment and get the full experience.
          </p>
        </div>
      )}

      {showComments && (
        <>
          {isAuthenticated && !replyToId && <CommentForm handleCommentSubmit={handleCommentSubmit} newComment={newComment} handleCommentChange={handleCommentChange} replyToId={replyToId} setReplyToId={setReplyToId} />}

          <div className="comments mt-6">

            {comments.map(comment => (
              <Comment 
                  key={comment.id} 
                  comment={comment} 
                  isAuthenticated={isAuthenticated} 
                  initiateReply={initiateReply} 
                  voteComment={voteComment} 
              />
          ))}
 
          </div>
        </>
      )}
    </div>
  );
}

export default CommentSection;
